import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store';
import { Dialog, DialogContent } from '../components/ui/dialog';
import {
  FileText, Plus, Loader2, Copy, MessageCircle, CheckCircle2, Clock,
  Trash2, ArrowRight, ArrowLeft, User, Phone, Receipt, Check, Search, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MSG_COBRANCA_PADRAO = 'Olá! Você tem uma cobrança pendente. Pague de forma rápida e segura com PIX ou cartão no link abaixo. Qualquer dúvida, estou à disposição!';
const MSG_PAGO_PADRAO = 'Pagamento confirmado com sucesso! ✅ Muito obrigado. Qualquer coisa, é só chamar!';

export default function GerarFaturaPage() {
  const { api, user } = useAuthStore();
  const [faturas, setFaturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criar, setCriar] = useState(false);
  const [editar, setEditar] = useState(null);
  const [busca, setBusca] = useState('');

  const podeAcessar = user && ['admin', 'master', 'reseller'].includes(user.role);

  const carregar = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/faturas-avulsas'); setFaturas(r.data.faturas || []); }
    catch {} finally { setLoading(false); }
  }, [api]);

  useEffect(() => { if (podeAcessar) carregar(); else setLoading(false); }, [carregar, podeAcessar]);

  if (!podeAcessar) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4"><FileText className="w-7 h-7 text-muted-foreground" /></div>
        <h2 className="text-lg font-semibold mb-1">Faturas indisponíveis</h2>
        <p className="text-sm text-muted-foreground">Disponível para revendedores, masters e administradores.</p>
      </div>
    );
  }

  const linkDe = (f) => `${window.location.origin}/fatura/${f.id}`;
  const cobrarWhatsapp = async (f) => {
    try { await api.post(`/faturas-avulsas/${f.id}/cobrar-whatsapp`); toast.success('Cobrança enviada no WhatsApp'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível enviar'); }
  };
  const excluir = async (f) => {
    if (!window.confirm('Excluir esta fatura?')) return;
    try { await api.delete(`/faturas-avulsas/${f.id}`); carregar(); } catch { toast.error('Erro ao excluir'); }
  };

  const q = busca.trim().toLowerCase();
  const filtradas = q ? faturas.filter(f => (f.nome || '').toLowerCase().includes(q) || (f.telefone || '').includes(q)) : faturas;
  const totalRecebido = faturas.filter(f => f.status === 'approved').reduce((a, f) => a + Number(f.valor || 0), 0);
  const pendentes = faturas.filter(f => f.status !== 'approved').length;

  return (
    <div className="min-h-full bg-gradient-to-b from-emerald-950/20 to-transparent">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center"><Receipt className="w-5 h-5 text-emerald-500" /></div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">Faturas</h1>
              <p className="text-xs text-muted-foreground">Cobre qualquer pessoa por PIX ou cartão</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-card border border-border p-4">
              <p className="text-xs text-muted-foreground">Recebido</p>
              <p className="text-2xl font-semibold text-emerald-500 tabular-nums mt-0.5">R$ {brl(totalRecebido)}</p>
            </div>
            <div className="rounded-2xl bg-card border border-border p-4">
              <p className="text-xs text-muted-foreground">Aguardando</p>
              <p className="text-2xl font-semibold tabular-nums mt-0.5">{pendentes}</p>
            </div>
          </div>
        </div>

        <button onClick={() => setCriar(true)}
          className="w-full rounded-2xl bg-emerald-500 text-emerald-950 font-semibold py-4 flex items-center justify-center gap-2 mb-6 transition-transform active:scale-[0.98] shadow-lg shadow-emerald-500/20">
          <Plus className="w-5 h-5" /> Nova cobrança
        </button>

        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-muted-foreground">Suas cobranças</h2>
          {faturas.length > 4 && (
            <div className="flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-muted/50 border border-border">
              <Search className="w-3.5 h-3.5 text-muted-foreground" />
              <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar" className="bg-transparent text-xs outline-none w-24" />
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-500" /></div>
        ) : filtradas.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-14 text-center">
            <Receipt className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">{q ? 'Nenhuma cobrança encontrada' : 'Nenhuma cobrança ainda'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtradas.map((f) => (
              <FaturaRow key={f.id} f={f} link={linkDe(f)} onWhatsapp={() => cobrarWhatsapp(f)} onExcluir={() => excluir(f)} onEditar={() => setEditar(f)} />
            ))}
          </div>
        )}
      </div>

      {criar && <CriarFatura api={api} onClose={() => setCriar(false)} onCreated={() => { setCriar(false); carregar(); }} />}
      {editar && <EditarFatura api={api} fatura={editar} onClose={() => setEditar(null)} onSaved={() => { setEditar(null); carregar(); }} />}
    </div>
  );
}

function FaturaRow({ f, link, onWhatsapp, onExcluir, onEditar }) {
  const [aberto, setAberto] = useState(false);
  const pago = f.status === 'approved';
  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden">
      <button onClick={() => setAberto(a => !a)} className="w-full flex items-center gap-3 p-3.5 text-left">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${pago ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-500'}`}>
          {pago ? <Check className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{f.nome}</p>
          <p className="text-xs text-muted-foreground">{pago ? 'Pago' : 'Aguardando pagamento'}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-semibold tabular-nums">R$ {brl(f.valor)}</p>
        </div>
      </button>
      {aberto && (
        <div className="px-3.5 pb-3.5 pt-1 border-t border-border/60 space-y-2.5">
          {f.descricao && <p className="text-xs text-muted-foreground">{f.descricao}</p>}
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Phone className="w-3 h-3" /> {f.telefone}</p>
          {!pago && (
<div className="flex gap-2 pt-1">
              <button onClick={() => { navigator.clipboard.writeText(link); toast.success('Link copiado'); }}
                className="flex-1 h-9 rounded-xl bg-muted text-sm font-medium flex items-center justify-center gap-1.5"><Copy className="w-4 h-4" /> Link</button>
              <button onClick={onWhatsapp}
                className="flex-1 h-9 rounded-xl bg-emerald-500/15 text-emerald-500 text-sm font-medium flex items-center justify-center gap-1.5"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
              <button onClick={onEditar} className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center" title="Editar"><Pencil className="w-4 h-4" /></button>
              <button onClick={onExcluir} className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          )}
          {pago && (
            <button onClick={onExcluir} className="text-xs text-rose-500 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Excluir do histórico</button>
          )}
        </div>
      )}
    </div>
  );
}

function CriarFatura({ api, onClose, onCreated }) {
  const [etapa, setEtapa] = useState(1);
  const [valor, setValor] = useState('0');
  const [form, setForm] = useState({
    nome: '', telefone: '', descricao: '',
    msg_cobranca: MSG_COBRANCA_PADRAO, msg_pagamento_confirmado: MSG_PAGO_PADRAO, notificar_whatsapp_ao_pagar: true,
  });
  const [saving, setSaving] = useState(false);
  const [criada, setCriada] = useState(null);

  const valorNum = parseInt(valor || '0', 10) / 100;

  const digitar = (d) => setValor(v => {
    if (d === 'back') return v.length <= 1 ? '0' : v.slice(0, -1);
    const nv = (v === '0' ? '' : v) + d;
    if (nv.length > 9) return v;
    return nv;
  });

  const criar = async () => {
    setSaving(true);
    try {
      const r = await api.post('/faturas-avulsas', { ...form, valor: valorNum });
      setCriada(r.data); setEtapa('done');
    } catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível criar'); }
    setSaving(false);
  };
  const cobrarWpp = async () => {
    try { await api.post(`/faturas-avulsas/${criada.id}/cobrar-whatsapp`); toast.success('Enviado no WhatsApp'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && (criada ? onCreated() : onClose())}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0">
        {etapa !== 'done' && (
          <div className="flex items-center gap-3 p-4 border-b border-border">
            {etapa > 1 ? (
              <button onClick={() => setEtapa(e => e - 1)} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
            ) : <div className="w-8" />}
            <div className="flex-1 text-center">
              <p className="text-sm font-medium">Nova cobrança</p>
              <p className="text-[11px] text-muted-foreground">Etapa {etapa} de 3</p>
            </div>
            <div className="w-8" />
          </div>
        )}

        {etapa === 1 && (
          <div className="p-6">
            <p className="text-center text-xs text-muted-foreground mb-1">Valor da cobrança</p>
            <div className="text-center mb-6">
              <span className="text-2xl font-medium text-muted-foreground align-top">R$ </span>
              <span className="text-5xl font-semibold tabular-nums tracking-tight">{brl(valorNum)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {['1','2','3','4','5','6','7','8','9','00','0','back'].map((k) => (
                <button key={k} onClick={() => digitar(k)}
                  className="h-14 rounded-xl bg-muted/60 hover:bg-muted text-lg font-medium flex items-center justify-center transition-colors active:scale-95">
                  {k === 'back' ? <ArrowLeft className="w-5 h-5" /> : k}
                </button>
              ))}
            </div>
            <button disabled={valorNum <= 0} onClick={() => setEtapa(2)}
              className="w-full mt-6 h-12 rounded-xl bg-emerald-500 text-emerald-950 font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.98]">
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {etapa === 2 && (
          <div className="p-6 space-y-4">
            <div className="text-center pb-2">
              <p className="text-xs text-muted-foreground">Cobrando</p>
              <p className="text-2xl font-semibold tabular-nums">R$ {brl(valorNum)}</p>
            </div>
            <Campo icon={User} label="Nome de quem vai pagar" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} placeholder="Nome completo" autoFocus />
            <Campo icon={Phone} label="WhatsApp (com DDI + DDD)" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v.replace(/\D/g, '') })} placeholder="5521999999999" inputMode="numeric" />
            <Campo icon={Receipt} label="Descrição (opcional)" value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} placeholder="Ex: Mensalidade, serviço..." />
            <button disabled={!form.nome.trim() || form.telefone.length < 10} onClick={() => setEtapa(3)}
              className="w-full h-12 rounded-xl bg-emerald-500 text-emerald-950 font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.98]">
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {etapa === 3 && (
          <div className="p-6 space-y-4">
            <div className="rounded-xl bg-muted/40 p-4 space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Valor</span><span className="font-semibold tabular-nums">R$ {brl(valorNum)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Para</span><span className="font-medium">{form.nome}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">WhatsApp</span><span className="font-medium">{form.telefone}</span></div>
            </div>
            <MsgCampo label="Mensagem da cobrança" value={form.msg_cobranca} onChange={(v) => setForm({ ...form, msg_cobranca: v })} hint="O link de pagamento é anexado automaticamente" />
            <MsgCampo label="Mensagem ao confirmar o pagamento" value={form.msg_pagamento_confirmado} onChange={(v) => setForm({ ...form, msg_pagamento_confirmado: v })} />
            <label className="flex items-center justify-between rounded-xl bg-muted/40 px-4 py-3">
              <span className="text-sm">Avisar no WhatsApp quando pagar</span>
              <button onClick={() => setForm({ ...form, notificar_whatsapp_ao_pagar: !form.notificar_whatsapp_ao_pagar })}
                className={`w-11 h-6 rounded-full transition-colors relative ${form.notificar_whatsapp_ao_pagar ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.notificar_whatsapp_ao_pagar ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </label>
            <button disabled={saving} onClick={criar}
              className="w-full h-12 rounded-xl bg-emerald-500 text-emerald-950 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-transform active:scale-[0.98]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gerar cobrança
            </button>
          </div>
        )}

        {etapa === 'done' && criada && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3 animate-in zoom-in duration-300">
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </div>
            <h3 className="text-lg font-semibold">Cobrança criada</h3>
            <p className="text-2xl font-semibold tabular-nums mt-1">R$ {brl(valorNum)}</p>
            <p className="text-sm text-muted-foreground mb-5">para {form.nome}</p>

            <div className="flex items-center gap-2 rounded-xl bg-muted/50 border border-border p-2 mb-3 max-w-full">
              <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate text-left px-1">{criada.link}</span>
              <button onClick={() => { navigator.clipboard.writeText(criada.link); toast.success('Link copiado'); }}
                className="h-8 px-3 rounded-lg bg-background text-xs font-medium flex items-center gap-1.5 shrink-0"><Copy className="w-3.5 h-3.5" /> Copiar</button>
            </div>
            <button onClick={cobrarWpp} className="w-full h-12 rounded-xl bg-emerald-500 text-emerald-950 font-semibold flex items-center justify-center gap-2 mb-2 transition-transform active:scale-[0.98]">
              <MessageCircle className="w-5 h-5" /> Enviar no WhatsApp
            </button>
            <button onClick={onCreated} className="w-full h-11 rounded-xl text-sm text-muted-foreground hover:bg-muted transition-colors">Concluir</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditarFatura({ api, fatura, onClose, onSaved }) {
  const [form, setForm] = useState({
    nome: fatura.nome || '', telefone: fatura.telefone || '', valor: String(fatura.valor ?? ''),
    descricao: fatura.descricao || '', msg_cobranca: fatura.msg_cobranca || '',
    msg_pagamento_confirmado: fatura.msg_pagamento_confirmado || '',
    notificar_whatsapp_ao_pagar: fatura.notificar_whatsapp_ao_pagar !== false,
  });
  const [saving, setSaving] = useState(false);
  const salvar = async () => {
    if (!form.nome.trim() || form.telefone.length < 10 || !parseFloat(form.valor)) { toast.error('Preencha nome, telefone e valor'); return; }
    setSaving(true);
    try { await api.put(`/faturas-avulsas/${fatura.id}`, { ...form, valor: parseFloat(form.valor) }); toast.success('Cobrança atualizada'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0">
        <div className="flex items-center gap-3 p-4 border-b border-border">
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
          <p className="flex-1 text-center text-sm font-medium">Editar cobrança</p>
          <div className="w-8" />
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Valor (R$)</label>
            <input type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })}
              className="w-full h-12 px-3.5 rounded-xl bg-muted/40 border border-border focus:border-emerald-500/50 outline-none text-lg font-semibold tabular-nums transition-colors" />
          </div>
          <Campo icon={User} label="Nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} placeholder="Nome" />
          <Campo icon={Phone} label="WhatsApp (DDI + DDD)" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v.replace(/\D/g, '') })} placeholder="5521999999999" inputMode="numeric" />
          <Campo icon={Receipt} label="Descrição" value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} placeholder="Opcional" />
          <MsgCampo label="Mensagem da cobrança" value={form.msg_cobranca} onChange={(v) => setForm({ ...form, msg_cobranca: v })} />
          <MsgCampo label="Mensagem ao confirmar" value={form.msg_pagamento_confirmado} onChange={(v) => setForm({ ...form, msg_pagamento_confirmado: v })} />
          <label className="flex items-center justify-between rounded-xl bg-muted/40 px-4 py-3">
            <span className="text-sm">Avisar no WhatsApp quando pagar</span>
            <button onClick={() => setForm({ ...form, notificar_whatsapp_ao_pagar: !form.notificar_whatsapp_ao_pagar })}
              className={`w-11 h-6 rounded-full transition-colors relative ${form.notificar_whatsapp_ao_pagar ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.notificar_whatsapp_ao_pagar ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </label>
          <button disabled={saving} onClick={salvar}
            className="w-full h-12 rounded-xl bg-emerald-500 text-emerald-950 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-transform active:scale-[0.98]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Salvar alterações
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Campo({ icon: Icon, label, value, onChange, placeholder, autoFocus, inputMode }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
      <div className="flex items-center gap-2.5 h-12 px-3.5 rounded-xl bg-muted/40 border border-border focus-within:border-emerald-500/50 transition-colors">
        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} inputMode={inputMode}
          className="flex-1 bg-transparent outline-none text-sm" />
      </div>
    </div>
  );
}

function MsgCampo({ label, value, onChange, hint }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
      <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl bg-muted/40 border border-border focus:border-emerald-500/50 outline-none text-sm p-3 transition-colors resize-none" />
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
